/**
 * Template runs, watched from anywhere in the app.
 *
 * Generation takes a minute or two, and until now leaving the page lost it:
 * the composer polled in its own component, so the result arrived to nobody
 * and the template was never saved — it sat inside `job.result` with nothing
 * pointing at it.
 *
 * So the watching lives above the router, like `AgentRunContext` does for the
 * agent, and the corner bubble reads from it. The run is server-side; the
 * page is not.
 *
 * ### It polls the cheap question, and only when there is one to ask
 *
 * `?active=1` is "is anything running", which is a filtered query rather than
 * a page of history. One check on mount — so a run started before a reload is
 * found — then every few seconds while something is in flight, and nothing at
 * all when there is not. A tab sitting on the dashboard for an hour asks once.
 *
 * ### Finishing is noticed by DISAPPEARING, and the notice SURVIVES a failure
 *
 * A job leaving the active list is how we learn it landed, which does not say
 * HOW it landed — so that is the one moment the full list is fetched, to find
 * out whether it was done, failed or stopped. One request per completion
 * rather than carrying every outcome on every poll.
 *
 * That made the outcome depend on one request succeeding at the one moment it
 * was asked for. The id was dropped from the watch set BEFORE the follow-up
 * ran, so a 502 on that second request lost the completion for good: the run
 * had finished, nothing was watching for it any more, and the corner never
 * reported it. Ids now stay in `pending` until an outcome is actually read,
 * so a failed attempt is retried on the next poll.
 *
 * ### A dropped poll is not news, and a failing server is not polled harder
 *
 * Every failure backs off (doubling, capped), and the delay resets on the
 * first answer. Polling a gateway that is returning 502 every three seconds
 * is how a struggling backend is kept struggling, and the request this makes
 * is the cheap one precisely so it can be dropped without consequence.
 *
 * ### Nothing is polled while the tab is hidden
 *
 * A browser freezes a background tab and cuts off whatever was in flight,
 * which surfaces as `ERR_NETWORK_IO_SUSPENDED` — a failure caused by asking
 * at all. So the loop stops when the tab is hidden and polls IMMEDIATELY when
 * it comes back, which is also the moment the answer is wanted: somebody has
 * just returned to look. `online` does the same after a connection drops.
 *
 * ### It checks the permission itself
 *
 * `manage_media_library`, rather than trusting where it is mounted. A hidden
 * launcher that still polls is a boundary the network can see through — the
 * same rule `AgentRunContext` follows.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { listJobs, markJobSeen } from './templateApi.js';
import { notifyDone } from './notify.js';

const POLL_MS = 3000;
const MAX_POLL_MS = 30_000;
const ACTIVE = new Set(['pending', 'running']);
// How far back "recently finished" reaches on a fresh load. A run nobody has
// looked at for a day is history, and the templates list still has it.
const RECENT_MS = 24 * 3600 * 1000;
const MAX_LANDED = 5;

/** What a run is called, by kind. */
export const RUN_NOUN = { generate: 'template', edit: 'chat edit', render: 'export', video: 'video' };

/**
 * One line saying how a run landed, the same in the bubble and in the
 * notification so the two never describe one run differently.
 */
export function landedTitle(job) {
  const noun = RUN_NOUN[job.kind] || 'template';
  if (job.outcome?.question) return `Your ${noun} has a question`;
  if (job.status === 'error') return `Your ${noun} did not finish`;
  if (job.status === 'cancelled') return `Your ${noun} was stopped`;
  if ((job.kind === 'render' || job.kind === 'video') && job.outcome?.file) return 'Saved to your media library';
  return `Your ${noun} is ready`;
}

/** Worth reporting on a fresh load: finished, unseen, and recent. */
const isRecentNews = (j, now = Date.now()) => !ACTIVE.has(j.status) && j.status !== 'cancelled'
  && !j.seen_at && (now - Date.parse(j.updated_at || j.created_at || 0)) < RECENT_MS;

const Ctx = createContext(null);

export const useTemplateRunsContext = () => useContext(Ctx);

export function TemplateRunsProvider({ children }) {
  // The same read `RequirePermission` makes, so the two cannot disagree about
  // who is allowed — admin passes on the expanded permission list.
  const permissions = useSelector((st) => st.auth?.permissions || []);
  const allowed = permissions.includes('admin') || permissions.includes('manage_media_library');

  const [active, setActive] = useState([]);
  // What landed and has not been looked at, newest first. Seeded from the
  // SERVER on load — `seen_at` lives on the job row — so a run that finished
  // while the app was closed, or in another window, is reported here too.
  // It used to be this tab's memory only, which is why opening the app in a
  // second window showed nothing about a run that had just finished.
  const [landed, setLanded] = useState([]);
  // Ids whose outcome we still owe somebody: everything seen running, plus
  // whatever the composer handed over. Emptied only when an outcome has been
  // READ, never when one was merely asked for.
  const pending = useRef(new Set());
  const fails = useRef(0);
  const [armed, setArmed] = useState(false);

  const check = useCallback(async () => {
    if (!allowed) return;
    let rows = [];
    try {
      rows = await listJobs({ active: true });
    } catch {
      // Not news. The backoff below is what stops this becoming a flood
      // against a gateway that is already failing.
      fails.current += 1;
      return;
    }
    fails.current = 0;
    const now = new Map(rows.filter((j) => ACTIVE.has(j.status)).map((j) => [j.id, j]));
    for (const id of now.keys()) pending.current.add(id);
    setActive([...now.values()]);

    const gone = [...pending.current].filter((id) => !now.has(id));
    if (!gone.length) return;

    // One request per completion: the active list cannot say HOW something
    // landed, only that it is no longer going.
    let all = [];
    try {
      all = await listJobs();
    } catch {
      fails.current += 1;
      return;   // the ids stay pending, so the next poll asks again
    }
    fails.current = 0;
    const finished = all.filter((j) => gone.includes(j.id));
    const answered = new Set(finished.map((j) => j.id));
    // Resolved by the request having SUCCEEDED, not by the id being found. An
    // id the list does not carry — deleted, or past the 25 it returns — has
    // been asked about and answered; keeping it pending would poll for it for
    // ever, which is the opposite mistake to the one above.
    for (const id of gone) pending.current.delete(id);
    if (!finished.length) return;
    setLanded((cur) => [...finished.filter((j) => j.status !== 'cancelled'),
      ...cur.filter((j) => !answered.has(j.id))].slice(0, MAX_LANDED));
    for (const job of finished) {
      if (job.status === 'cancelled') continue;
      notifyDone({
        title: landedTitle(job),
        body: job.outcome?.question || job.template?.name || job.summary || '',
        // The tag the server's push uses too, so the page's notification and
        // a push that also lands collapse into one.
        tag: `tpl-job-${job.id}`,
      });
    }
  }, [allowed]);

  // On mount: what is running (the check), and what finished unseen — from
  // the server, so a reload or a second window reports it as well.
  useEffect(() => {
    if (!allowed) return;
    check();
    listJobs().then((all) => {
      const now = Date.now();
      setLanded((cur) => {
        const have = new Set(cur.map((j) => j.id));
        return [...cur, ...all.filter((j) => isRecentNews(j, now) && !have.has(j.id))].slice(0, MAX_LANDED);
      });
    }).catch(() => {});
  }, [allowed, check]);

  // And then only while something is going. `armed` keeps the timer alive for
  // one cycle after a run is started from the composer, before the first poll
  // has seen it — otherwise starting one and navigating away immediately
  // leaves nothing watching.
  const going = active.length > 0 || pending.current.size > 0;
  useEffect(() => {
    if (!allowed || (!going && !armed)) return undefined;
    // A self-scheduling timeout rather than an interval, for two reasons: the
    // delay has to grow while requests are failing, and an interval fires
    // whether or not the last request came back — so a slow server collects
    // overlapping polls from every client, which is the behaviour that turns
    // a slow endpoint into a failing one.
    let timer = null;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      if (!document.hidden) await check();
      if (stopped) return;
      timer = setTimeout(tick, Math.min(POLL_MS * (2 ** fails.current), MAX_POLL_MS));
    };
    timer = setTimeout(tick, POLL_MS);

    // Back from a hidden tab, or back online: ask at once, and forget the
    // backoff — the failures were almost certainly the suspension itself.
    const wake = () => {
      if (document.hidden) return;
      fails.current = 0;
      clearTimeout(timer);
      tick();
    };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
    };
  }, [allowed, going, armed, check]);

  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), POLL_MS * 4);
    return () => clearTimeout(t);
  }, [armed]);

  const value = useMemo(() => ({
    allowed,
    active,
    landed,
    /**
     * Called by the composer when it starts one, so the timer runs at once.
     *
     * The id matters: a run that finishes before the first poll ever sees it
     * active would otherwise never "disappear" from anything, and the corner
     * would not report it. Naming it here makes the outcome owed from the
     * moment it starts rather than from the moment it is first observed.
     */
    watch: (id) => {
      if (id) pending.current.add(id);
      setArmed(true);
      check();
    },
    /**
     * Dismissed is SEEN, on the server: otherwise the next window, or the
     * next load, reports it all over again.
     */
    dismiss: (id) => {
      setLanded((cur) => cur.filter((j) => j.id !== id));
      markJobSeen(id).catch(() => {});
    },
    /** Every run, running or landed, that belongs to one saved template. */
    forTemplate: (templateId) => ({
      active: active.filter((j) => j.template?.id === templateId),
      landed: landed.filter((j) => j.template?.id === templateId),
    }),
    refresh: check,
  }), [allowed, active, landed, check]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export default TemplateRunsProvider;
